import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { CUDL_PROVIDER_CONFIG, PushToFIStatus, ROUTE_ONE_PROVIDER_CONFIG } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { SOCKET_EVENT_IGNORED } from '../../actionTypes/socketActionTypes';
import { updatePushToFI } from '../pushToFIActionCreators';
import { updatePushToRouteOne } from '../pushToRouteOneActionCreators';
import * as offerSelectors from '../../selectors/offerSelectors';
import * as pushToFISelectors from '../../selectors/pushToFISelectors';
import { createSocketLoggingContext } from './loggingContext';
import type { TopicHandler } from './types';

export const handlePushToFI: TopicHandler = (state, event) => {
    const { data } = event;
    if (!data.eventTime || !data.eventKeyData) return null;

    // Fan-out safety: when the originating event had no dxgId, the BFF broadcasts it
    // with no filter, so it reaches every pushToFI subscriber. Confirm the event
    // belongs to the open deal by matching dealRefId before acting on it. Messages that
    // carry a dxgId were routed to this deal by the websocket service and are trusted.
    if (!data.eventKeyData.dealXgDealId) {
        const openDealRefId = offerSelectors.getDealRefId(state);
        if (!openDealRefId || data.eventTransactionId !== openDealRefId) {
            logNewRelicPageAction('MV:PushToFI:WebSocketReceived:FanOutFiltered', {
                ...createSocketLoggingContext(state, event),
                reason: openDealRefId ? 'dealref_mismatch' : 'no_open_dealref'
            });
            return { type: SOCKET_EVENT_IGNORED, payload: event };
        }
    }

    const targetPlatform = data.eventKeyData.targetPlatform;

    const isGenericPathForR1J =
        targetPlatform === ROUTE_ONE_PROVIDER_CONFIG.targetPlatformId &&
        featureToggleSelectors.isGenericPushToFiForR1JEnabled(state);

    // CUDL (CDL), plus R1J when the generic path is enabled
    if (targetPlatform === CUDL_PROVIDER_CONFIG.targetPlatformId || isGenericPathForR1J) {
        // Only accept WS updates while a push the user initiated is in flight (status === Waiting).
        // Three NotWaiting sub-cases are tracked separately so NR shows how often each occurs:
        //   - modal_dismissed: slice exists with status=Ready — user dismissed before WS arrived
        //   - stale_duplicate: slice exists with terminal status — a result is already on screen
        //   - unsolicited:     no slice at all — user never initiated a push this session
        const providerState = pushToFISelectors.getPushToFIStatus(state, targetPlatform);
        const currentStatus = providerState?.status;
        if (currentStatus !== PushToFIStatus.Waiting) {
            const reason = providerState
                ? currentStatus === PushToFIStatus.Ready
                    ? 'modal_dismissed'
                    : 'stale_duplicate'
                : 'unsolicited';
            logNewRelicPageAction('MV:PushToFI:WebSocketReceived:NotWaiting', {
                ...createSocketLoggingContext(state, event),
                targetPlatform,
                currentStatus: currentStatus ?? 'none',
                reason
            });
            return { type: SOCKET_EVENT_IGNORED, payload: event };
        }

        return updatePushToFI(targetPlatform, {
            timestamp: data.eventTime,
            message: data.payload?.message,
            ok: data.eventName === 'Deal:Success'
        });
    }

    // RouteOne (R1J) — legacy `state.pushToRouteOne` slice / legacy modal.
    if (targetPlatform === ROUTE_ONE_PROVIDER_CONFIG.targetPlatformId) {
        return updatePushToRouteOne({
            timestamp: data.eventTime,
            dealXgVersion: data.eventKeyData.dealXgDealVersion,
            message: data.payload.message,
            ok: data.eventName === 'Contracting:SavedSuccessfully'
        });
    }

    // Unrecognized platform (missing, malformed, or not CDL/R1J) → log and ignore.
    logNewRelicPageAction('MV:PushToFI:WebSocketReceived:InvalidTargetPlatform', {
        ...createSocketLoggingContext(state, event),
        targetPlatformValue: typeof targetPlatform === 'string' ? targetPlatform : 'non-string',
        reason: 'invalid_target_platform'
    });
    return { type: SOCKET_EVENT_IGNORED, payload: event };
};
